import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from 'shadcn-ui'
import { ShieldAlert, ArrowLeft, Home, CornerUpRight } from 'lucide-react'
import { useMemo } from 'react'

// Route: /access-denied?redirect=%2Finventory%2Fproduct-model
// Reads the optional `redirect` query and presents clear actions.
export const Route = createFileRoute('/access-denied')({
  component: AccessDeniedRoute,
})

type Search = {
  redirect?: string;
  permissionRequired?: string;
  roleRequired?: string;
  currentPermissions?: string;
  currentRoles?: string;
}

function AccessDeniedRoute() {
  const navigate = useNavigate()
  const search = Route.useSearch() as Search

  const target = useMemo(() => {
    if (!search?.redirect) return undefined
    try {
      return decodeURIComponent(search.redirect)
    } catch {
      return search.redirect
    }
  }, [search?.redirect])

  // Parse required permissions and roles (comma-separated)
  const requiredPermissions = useMemo(
    () => (search?.permissionRequired ? search.permissionRequired.split(',').filter(Boolean) : []),
    [search?.permissionRequired],
  )
  const requiredRoles = useMemo(
    () => (search?.roleRequired ? search.roleRequired.split(',').filter(Boolean) : []),
    [search?.roleRequired],
  )

  // Parse current roles/permissions (comma-separated)
  const currentPermissions = useMemo(
    () => (search?.currentPermissions ? search.currentPermissions.split(',').filter(Boolean) : []),
    [search?.currentPermissions],
  )
  const currentRoles = useMemo(
    () => (search?.currentRoles ? search.currentRoles.split(',').filter(Boolean) : []),
    [search?.currentRoles],
  )

  return (
    <div className="min-h-[60vh] w-full grid place-items-center px-4">
      <Card className="max-w-xl w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="rounded-md border p-2">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>Access denied</CardTitle>
              <CardDescription>
                You don&apos;t have permission to view this page.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {target ? (
            <p className="text-sm text-muted-foreground">
              Requested page: <span className="font-mono">{target}</span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              If you believe this is a mistake, contact an administrator.
            </p>
          )}
          {(requiredRoles.length > 0 || requiredPermissions.length > 0 || currentRoles.length > 0 || currentPermissions.length > 0) && (
            <div className="mt-3 space-y-2">
              {requiredRoles.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Required role(s): <span className="font-mono">{requiredRoles.join(', ')}</span>
                </p>
              )}
              {requiredPermissions.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Required permission(s): <span className="font-mono">{requiredPermissions.join(', ')}</span>
                </p>
              )}
              {currentRoles.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Your role(s): <span className="font-mono">{currentRoles.join(', ')}</span>
                </p>
              )}
              {currentPermissions.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Your permission(s): <span className="font-mono">{currentPermissions.join(', ')}</span>
                </p>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-wrap gap-2 justify-end">
          <Button variant="outline" onClick={() => navigate({ to: '..', search: (s) => s, replace: true })}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
          <Button variant="outline" onClick={() => navigate({ to: '/dashboard', replace: true })}>
            <Home className="mr-2 h-4 w-4" />
            Dashboard
          </Button>
          {target && (
            <Button onClick={() => navigate({ to: target as any, replace: true })}>
              <CornerUpRight className="mr-2 h-4 w-4" />
              Go to requested page
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  )
}
